import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, PageHeader, PaginationControl, RouteLoadingIndicator } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import Footer from '@web/src/features/footer/footer'
import PublicationCard, {
  type PublicationCardLayoutMode,
} from '@web/src/features/publication/publication-card/publication-card'
import {
  type PublicationListFilterFormValues,
  publicationListFilterFormValuesFromResolvedParams,
} from '@web/src/features/publication/publication-list-filter-types'
import PublicationListSideNav from '@web/src/features/publication/publication-list-side-nav/publication-list-side-nav'
import PublicationListTopNav from '@web/src/features/publication/publication-list-top-nav/publication-list-top-nav'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { useTranslation } from '@web/src/lib/intl/react'
import { BookOpenIcon, LayoutGridIcon, LayoutListIcon, PlusIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AttachedMediaInfo, PublicationInfo } from 'src/lib/fragments'
import type { PublicationSortEnum, PublicationsQuery, PublicationsQueryVariables } from 'types/graphql'
import { z } from 'zod'
import { publicationListPageHeadFromLoaderData } from './list.seo'

const PUBLICATIONS_QUERY: TypedDocumentNode<PublicationsQuery, PublicationsQueryVariables> = gql`
  query PublicationsQuery($page: Int, $filter: PublicationFilter, $sortBy: PublicationSortEnum) {
    publicationsPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        ...PublicationInfo
        firstMedia {
          ...AttachedMediaInfo
        }
      }
      count
      perPage
    }
    publicationTags {
      id
      name
    }
  }
  ${PublicationInfo}
  ${AttachedMediaInfo}
`

type PublicationListSearchInput = {
  page?: number
  search?: string
  tags?: string[]
  sortBy?: string
  myPublications?: boolean
  needsReview?: boolean
}

function resolvePublicationListSearchParams(search: PublicationListSearchInput) {
  return {
    page: search.page ?? 1,
    search: search.search,
    tags: search.tags,
    sortBy: search.sortBy,
    myPublications: search.myPublications ?? false,
    needsReview: search.needsReview ?? false,
  }
}

const useFixedParams = () => {
  const search = useSearch({ strict: false })
  return useMemo(() => resolvePublicationListSearchParams(search), [search])
}

export const Route = createFileRoute('/(public)/(main-layout)/publication/list')({
  component: PublicationSearchPage,
  staticData: {
    hideFooter: true,
  },
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    search: z.string().optional(),
    tags: z.array(z.string()).optional(),
    sortBy: z.string().optional(),
    myPublications: z.coerce.boolean().optional(),
    needsReview: z.coerce.boolean().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        search: '',
        tags: [],
        sortBy: 'RATING',
        myPublications: false,
        needsReview: false,
      }),
    ],
  },
  head: ({ loaderData, match }) => publicationListPageHeadFromLoaderData(loaderData, match.search),
  loaderDeps: ({ search }) => resolvePublicationListSearchParams(search),
  loader: async ({ context, deps: d }) => {
    const deps = d as ReturnType<typeof resolvePublicationListSearchParams>
    const variables: PublicationsQueryVariables = {
      page: deps.page,
      filter: {
        search: deps.search,
        tags: deps.tags,
        myPublications: deps.myPublications,
        needsReview: deps.needsReview,
      },
      sortBy: (deps.sortBy as PublicationSortEnum) ?? 'RATING',
    }

    const { queryRef, queryData } = await queryAndPreload(context, PUBLICATIONS_QUERY, { variables })
    const entries = queryData?.publicationsPage?.entries ?? []
    return {
      queryRef,
      seoList: {
        items: entries.slice(0, 24).map((e) => ({
          name: e.name?.trim() || 'Publication',
          path: `/publication/${e.slug ?? e.id}`,
          imageUrl: e.firstMedia?.imageUrlMedium,
        })),
        totalCount: queryData?.publicationsPage?.count ?? 0,
      },
    }
  },
})

function CreatePublicationButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const handleClick = () => {
    if (hasPermission('publication:create')) {
      navigate({ to: '/publication/create' })
    } else {
      navigate({ to: '/supporter' })
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={<Button aria-label={t('Create a new publication')} onClick={handleClick} size="icon" type="button" />}
      >
        <PlusIcon />
      </TooltipTrigger>
      <TooltipContent>{t('Create a new publication')}</TooltipContent>
    </Tooltip>
  )
}

function LayoutToggleButton({ layout, onToggle }: { layout: PublicationCardLayoutMode; onToggle: () => void }) {
  const { t } = useTranslation()
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={
              layout === 'compact' ? t('Switch to stacked publication cards') : t('Switch to compact publication cards')
            }
            onClick={onToggle}
            size="icon"
            type="button"
            variant="secondary"
          />
        }
      >
        {layout !== 'compact' ? <LayoutGridIcon className="size-4" /> : <LayoutListIcon className="size-4" />}
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {layout === 'compact' ? t('Show image above details') : t('Show image beside details')}
      </TooltipContent>
    </Tooltip>
  )
}

function PublicationSearchPage() {
  const { t } = useTranslation()
  const params = useFixedParams()
  const { queryRef } = Route.useLoaderData()
  const { data, error } = useReadQuery(queryRef as QueryRef<PublicationsQuery>)

  const search = useSearch({ strict: false })
  const searchRef = useRef(search)
  searchRef.current = search
  const navigate = useNavigate()
  const { hasPermission, isAuthenticated, loading: authLoading } = useAuth()
  const isRoutePending = useRouterState({ select: (s) => s.status === 'pending' })
  const [filterNavigatePending, setFilterNavigatePending] = useState(false)

  useEffect(() => {
    if (!isRoutePending) {
      setFilterNavigatePending(false)
    }
  }, [isRoutePending])

  useEffect(() => {
    if (authLoading) {
      return
    }
    if (!isAuthenticated && params.page > 2) {
      navigate({ to: '/auth/signup' } as never)
    }
  }, [authLoading, isAuthenticated, navigate, params.page])

  const isFilterLoading = filterNavigatePending && isRoutePending

  const [cardLayout, setCardLayout] = useState<PublicationCardLayoutMode>('regular')

  const resolvedPublicationsPage =
    data?.publicationsPage ??
    ({
      entries: [],
      count: 0,
      perPage: 1,
    } as NonNullable<PublicationsQuery['publicationsPage']>)
  const resolvedPublicationTags = data?.publicationTags ?? []

  const listFilterDefaults = useMemo(
    (): PublicationListFilterFormValues =>
      publicationListFilterFormValuesFromResolvedParams({
        search: params.search,
        tags: params.tags,
        sortBy: params.sortBy,
        myPublications: params.myPublications,
        needsReview: params.needsReview,
      }),
    [params.search, params.tags, params.sortBy, params.myPublications, params.needsReview]
  )

  const listFilterResetValues = useMemo(
    (): PublicationListFilterFormValues =>
      publicationListFilterFormValuesFromResolvedParams({
        search: undefined,
        tags: undefined,
        sortBy: undefined,
        myPublications: false,
        needsReview: false,
      }),
    []
  )

  const sortFormMethods = useForm<{ sortBy?: string }>({
    defaultValues: { sortBy: params.sortBy },
  })

  useEffect(() => {
    sortFormMethods.reset({ sortBy: params.sortBy })
  }, [params.sortBy, sortFormMethods])

  const applyListFilters = useCallback(
    (values: PublicationListFilterFormValues, options: { includeSort: boolean }) => {
      setFilterNavigatePending(true)
      navigate({
        search: {
          ...searchRef.current,
          page: 1,
          search: values.search || undefined,
          tags: values.tags?.length > 0 ? values.tags : undefined,
          myPublications: values.myPublications || undefined,
          needsReview: values.needsReview || undefined,
          ...(options.includeSort ? { sortBy: values.sortBy || undefined } : {}),
        },
        replace: true,
      })
    },
    [navigate]
  )

  const handleSortChange = useCallback(() => {
    const sortBy = sortFormMethods.getValues('sortBy')
    navigate({
      search: {
        ...searchRef.current,
        page: 1,
        sortBy: sortBy || undefined,
      },
      replace: true,
    })
  }, [navigate, sortFormMethods])

  const handlePageChange = useCallback(
    (nextPage: number): void => {
      if (!isAuthenticated && nextPage > 2) {
        const nextUrl = new URL(window.location.href)
        nextUrl.searchParams.set('page', String(nextPage))
        navigate({ to: '/auth/signup' } as never)
        return
      }
      navigate({ search: { ...searchRef.current, page: nextPage }, replace: true })
    },
    [isAuthenticated, navigate]
  )

  const handleReset = useCallback(() => {
    navigate({ to: '/publication/list', replace: true } as Parameters<typeof navigate>[0])
  }, [navigate])

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <div className="flex w-full flex-col md:flex-row">
      <aside className="hidden w-fit shrink-0 overflow-visible md:sticky md:top-14 md:flex md:h-[calc(100dvh-3.5rem)] md:flex-col md:self-start">
        <PublicationListSideNav
          filterDefaults={listFilterDefaults}
          filterResetValues={listFilterResetValues}
          hasPermission={hasPermission}
          isAuthenticated={isAuthenticated}
          onFiltersApply={(values) => applyListFilters(values, { includeSort: false })}
          onReset={handleReset}
          tags={resolvedPublicationTags}
        />
      </aside>

      <div className="flex w-full min-w-0 flex-1 flex-col">
        <RouteLoadingIndicator loading={isFilterLoading} size="xs" variant="progress" />
        <div className="w-full max-w-full px-4 py-2 md:px-8 lg:px-16">
          <PageHeader
            breadcrumbItems={[{ label: t('Home'), to: '/' }, { label: t('Discover Cliterature') }]}
            className="mb-0 items-stretch pb-4 md:mb-4"
            highlightTitle={t('Cliterature')}
            preTitle={t('Discover')}
            rightChild={
              <>
                <div className="flex flex-col items-end gap-3 md:hidden">
                  {isAuthenticated ? <CreatePublicationButton /> : null}
                </div>
                <div className="hidden h-full flex-col items-end gap-3 md:flex">
                  {isAuthenticated ? (
                    <div className="flex gap-2">
                      <CreatePublicationButton />
                    </div>
                  ) : null}

                  <div className="mt-auto">
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 text-nowrap font-medium text-muted-foreground text-sm">
                        {t('Sort By:')}
                      </span>
                      <Form formMethods={sortFormMethods} suppressHydrationWarning>
                        <NativeSelectFormField name="sortBy" onValueChange={handleSortChange}>
                          <option value="AUTHOR">{t('Author')}</option>
                          <option value="LATEST">{t('Latest')}</option>
                          <option value="RATING">{t('Rating')}</option>
                          <option value="SERIES">{t('Series')}</option>
                          <option value="TITLE">{t('Title')}</option>
                        </NativeSelectFormField>
                      </Form>
                      <LayoutToggleButton
                        layout={cardLayout}
                        onToggle={() => setCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                      />
                    </div>
                  </div>
                </div>
              </>
            }
            subtitle={t(
              'Browse a curated collection of naughty literature — filter by tags and find your next favorite read.'
            )}
          />

          <div className="mb-6 w-full md:hidden">
            <PublicationListTopNav
              filterDefaults={listFilterDefaults}
              filterResetValues={listFilterResetValues}
              hasPermission={hasPermission}
              isAuthenticated={isAuthenticated}
              onFiltersApply={(values) => applyListFilters(values, { includeSort: true })}
              onReset={handleReset}
              rightBarChildren={
                <>
                  {isAuthenticated && <CreatePublicationButton />}
                  <LayoutToggleButton
                    layout={cardLayout}
                    onToggle={() => setCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                  />
                </>
              }
              tags={resolvedPublicationTags}
            />
          </div>

          {resolvedPublicationsPage.entries.length > 0 ? (
            <div className="flex w-full flex-col gap-0">
              <div
                className={
                  cardLayout === 'compact'
                    ? 'grid w-full grid-cols-1 items-stretch gap-4 md:gap-6'
                    : 'grid w-full grid-cols-1 items-stretch gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4'
                }
              >
                {resolvedPublicationsPage.entries?.map((publication) => (
                  <div className="flex h-full w-full min-w-0 flex-col" key={publication.id}>
                    <PublicationCard cardLayout={cardLayout} publication={publication} />
                  </div>
                ))}
              </div>

              <PaginationControl
                count={resolvedPublicationsPage.count ? resolvedPublicationsPage.count : 1}
                onPageChange={(e) => handlePageChange(e.page)}
                page={params.page}
                pageSize={resolvedPublicationsPage.perPage ? resolvedPublicationsPage.perPage : 1}
              />
            </div>
          ) : (
            <Empty className="w-full pb-20">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <BookOpenIcon className="size-8" />
                </EmptyMedia>
                <EmptyTitle>{t('No publications found')}</EmptyTitle>
                <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </div>

        <Footer />
      </div>
    </div>
  )
}
